import React, {
  useEffect,
  useState,
} from "react";

import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
} from "firebase/firestore";

import {
  ref,
  getBlob,
} from "firebase/storage";
import {
  Link as RouterLink,
} from "react-router-dom";
import {
  Badge,
  Box,
  Button,
  Divider,
  Flex,
  Link,
  Stack,
  Text,
  useColorModeValue,
  useToast,
} from "@chakra-ui/react";

import moment from "moment";

import {
  db,
  storage,
} from "../../../firebase/config";
const renderReplyContent = (content) => {
  const normalizedContent = String(
    content || ""
  )
    .replace(/\\\//g, "/")
    .replace(/\\([<>])/g, "$1");

  const legacyLinkPattern =
    /<a\s+[^>]*href=["']([^"']+)["'][^>]*>(.*?)<\/a>/gi;

  const renderedContent = [];
  let lastIndex = 0;
  let match;

  while (
    (match = legacyLinkPattern.exec(
      normalizedContent
    )) !== null
  ) {
    if (match.index > lastIndex) {
      renderedContent.push(
        normalizedContent.slice(
          lastIndex,
          match.index
        )
      );
    }

    const href = match[1].replace(
      /&amp;/g,
      "&"
    );

    const linkText = match[2]
      .replace(/<[^>]*>/g, "")
      .trim();

    const legacyTicketMatch =
      href.match(
        /\/jsst-ticket\/(\d+)/i
      );

    if (legacyTicketMatch) {
      const legacyTicketId =
        legacyTicketMatch[1];

      renderedContent.push(
        <Link
          as={RouterLink}
          key={`${legacyTicketId}-${match.index}`}
          to={`/projects/legacy-js-ticket-${legacyTicketId}`}
          color="teal.600"
          fontWeight="semibold"
          textDecoration="underline"
        >
          {linkText ||
            `Project #${legacyTicketId}`}
        </Link>
      );
    } else {
      /*
       * Unknown legacy HTML is displayed as text rather
       * than being injected into the page.
       */
      renderedContent.push(
        linkText || href
      );
    }

    lastIndex =
      legacyLinkPattern.lastIndex;
  }

  if (
    lastIndex <
    normalizedContent.length
  ) {
    renderedContent.push(
      normalizedContent.slice(
        lastIndex
      )
    );
  }

  return renderedContent.length > 0
    ? renderedContent
    : normalizedContent;
};

export default function RepliesDashboard({
  projectId,
}) {
  const [replies, setReplies] =
    useState([]);

  const toast = useToast();

  const cardBg = useColorModeValue(
    "white",
    "gray.800"
  );

  useEffect(() => {
    if (!projectId) {
      setReplies([]);
      return undefined;
    }

    const repliesQuery = query(
      collection(db, "replies"),
      where(
        "projectId",
        "==",
        projectId
      ),
      orderBy("createdAt", "desc")
    );

    const unsubscribe = onSnapshot(
      repliesQuery,
      (snapshot) => {
        const data = snapshot.docs.map(
          (replyDocument) => ({
            id: replyDocument.id,
            ...replyDocument.data(),
          })
        );

        setReplies(data);
      },
      (error) => {
        console.error(
          "Replies listener failed:",
          error
        );

        toast({
          title:
            "Replies could not be loaded",
          description:
            "Please refresh the page. If the problem continues, contact an administrator.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    );

    return unsubscribe;
  }, [projectId, toast]);

  const handleAttachmentDownload =
    async (attachment) => {
      if (!attachment.path) {
        if (attachment.url) {
          window.open(
            attachment.url,
            "_blank",
            "noopener,noreferrer"
          );

          return;
        }

        toast({
          title: "File unavailable",
          description:
            "This older attachment does not have a valid file location.",
          status: "error",
          duration: 4000,
          isClosable: true,
        });

        return;
      }

      try {
        const attachmentReference =
          ref(
            storage,
            attachment.path
          );

        const fileBlob =
          await getBlob(
            attachmentReference
          );

        const temporaryUrl =
          URL.createObjectURL(
            fileBlob
          );

        const downloadLink =
          document.createElement("a");

        downloadLink.href =
          temporaryUrl;

        downloadLink.download =
          attachment.originalFileName ||
          attachment.name ||
          "reply-attachment";

        document.body.appendChild(
          downloadLink
        );

        downloadLink.click();
        downloadLink.remove();

        window.setTimeout(() => {
          URL.revokeObjectURL(
            temporaryUrl
          );
        }, 1000);
      } catch (error) {
        console.error(
          "Reply attachment download failed:",
          error
        );

        toast({
          title:
            "File could not be downloaded",
          description:
            "You may not have permission to access this file, or the file may no longer exist.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    };

  return (
    <Stack spacing={4}>
      {replies.length === 0 && (
        <Text color="gray.500">
          No replies yet.
        </Text>
      )}

      {replies.map((reply) => (
        <Box
          key={reply.id}
          p={4}
          bg={cardBg}
          borderRadius="md"
          shadow="sm"
          borderWidth="1px"
        >
          <Text
            fontWeight="semibold"
            mb={2}
          >
            {reply.createdBy || "User"}
          </Text>

          <Text
  mb={3}
  whiteSpace="pre-wrap"
>
  {renderReplyContent(reply.reply)}
</Text>

          {reply.attachments?.length >
            0 && (
            <Box mb={3}>
              <Text
                fontSize="sm"
                fontWeight="semibold"
                mb={2}
              >
                Attachments
              </Text>

              <Stack spacing={2}>
                {reply.attachments.map(
                  (
                    attachment,
                    index
                  ) => (
                    <Button
                      key={
                        attachment.path ||
                        attachment.url ||
                        `${
                          attachment.originalFileName ||
                          attachment.name ||
                          "attachment"
                        }-${index}`
                      }
                      size="sm"
                      variant="outline"
                      justifyContent="flex-start"
                      height="auto"
                      minH="32px"
                      py={2}
                      whiteSpace="normal"
                      textAlign="left"
                      onClick={() =>
                        handleAttachmentDownload(
                          attachment
                        )
                      }
                    >
                      📎{" "}
                      {attachment.originalFileName ||
                        attachment.name ||
                        "Download attachment"}
                    </Button>
                  )
                )}
              </Stack>
            </Box>
          )}

          <Divider mb={2} />

          <Flex
            justify="space-between"
            align="center"
            gap={3}
          >
            <Text
              fontSize="sm"
              color="gray.500"
            >
              {reply.createdAt
                ? moment(
                    reply.createdAt.toDate?.() ||
                      reply.createdAt
                  ).format(
                    "MMMM Do YYYY, h:mm a"
                  )
                : "Unknown time"}
            </Text>

            <Badge colorScheme="blue">
              Reply
            </Badge>
          </Flex>
        </Box>
      ))}
    </Stack>
  );
}